import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import { Card, Tag } from 'antd';
import {
	getTodaysApy,
	retrieveCustodialWallets,
} from '../methods/redux/reducers/general';
import { defaultImage } from '../methods/utils/general-utils';
import CustodialWallets from './CustodialWallets';
import { useAppDispatch, useAppSelector } from '../methods/redux/hooks';
import {
	getBlockchainNetworks,
	getContractAddress,
	getCurrencies,
} from '../methods/redux/reducers/currency';
import TodaysApy from './TodaysApy';
import {
	getSavingsSum,
	getTodaysStats,
	getBvnEaseId,
	getPaymentGateways,
} from '../methods/redux/reducers/dashboard';
import ReportHighlights from './ReportHightlights';
import StatusCount from '../transactions/withdrawal-request/StatusCount';
import InvestmentWithdrawalStatusCount from '../core-modules/investment/InvestmentWithdrawalCounts';
import { getWithdrawalRequestCount } from '../methods/redux/reducers/withdrawal-requests';
import SwapStatusCount from '../core-modules/swap/SwapCountByStatus';
import { retrieveSwapRequestCount } from '../methods/redux/reducers/swap';
import NativeNumber from '../component/Numeral';
import CustodialUsers from './CustodialUsers';
import LoanStatusCount from '../core-modules/loan/LoanStatusCount';
import UrgentNotifications from './UrgentNotifications';
import { retrieveToken } from '../methods/utils/auth';
import { retrieveLoanStatuses } from '../methods/redux/reducers/loan';
import { investmentWithdrawalStatusCount } from '../methods/redux/reducers/investment';
import OnlineAdmins from './OnlineAdmins';

const LandingStyle = styled.div`
	& .two-status {
		display: grid;
		grid-template-columns: 1fr;
		grid-gap: 20px;

		@media (min-width: 768px) {
			grid-template-columns: 1fr 1fr;
		}
	}
	& .todays-stats {
		display: flex;
		flex-wrap: wrap;

		& > * {
			display: inline-block;
			min-width: 200px;
			max-width: 300px;
			padding: 10px 10px 10px 0;

			& .stat-label {
				font-size: 10px;
				color: #333;
				letter-spacing: 0.6px;
				font-weight: 400;
				margin: 0;
				padding: 0;
				line-height: 1.5;
				text-transform: uppercase;
			}

			& .stat-value {
				font-weight: 600;
				font-size: 21px;
				margin: 0;
				padding: 0;
				line-height: 1;
				margin-bottom: 5px;
			}

			& .amount {
				background-color: #f2f2f2;
				display: inline-flex;
				align-items: center;
				margin: 0 2px;
				padding: 0 3px;
				font-size: 12px;

				& img {
					width: 10px;
					height: 10px;
					border-radius: 50%;
					margin-right: 4px;
				}
			}
		}
	}
`;

const LandingPage = () => {
	const dispatch = useAppDispatch();

	const { custodialWallets } = useAppSelector((store) => store.general);
	const { today, bvnEaseId, paymentGateways } = useAppSelector(
		(store) => store.dashboard
	);
	const { investmentWithdrawalStatus } = useAppSelector(
		(store) => store.investment
	);

	const [state, setState] = useState({
		fixed: 0,
		flexible: 0,
		stake: 0,
	});

	console.info('paymentGateways', paymentGateways);

	useEffect(() => {
		dispatch(retrieveCustodialWallets());
		dispatch(getBlockchainNetworks());
		dispatch(getContractAddress({}));
		dispatch(getCurrencies({}));
		dispatch(getTodaysApy());
		dispatch(getTodaysStats());
		dispatch(getSavingsSum({}));
		dispatch(getBvnEaseId());
		dispatch(getPaymentGateways());
		dispatch(getWithdrawalRequestCount({}));
		dispatch(retrieveSwapRequestCount({}));
		dispatch(retrieveLoanStatuses({}));
		dispatch(investmentWithdrawalStatusCount({}));
	}, []);

	useEffect(() => {
		const fixedSum = today.fixed.today
			.map(
				(currencyData: any) =>
					Number(currencyData.totalAmount) *
					Number(currencyData.currencyId?.usdValue || 0)
			)
			.reduce((a, prev) => a + prev, 0);

		const flexiSum = today.flexible.today
			.map(
				(currencyData: any) =>
					Number(currencyData.totalAmount) *
					Number(currencyData.currencyId?.usdValue || 0)
			)
			.reduce((a, prev) => a + prev, 0);

		const stakeSum = today.stakes.today
			.map(
				(currencyData: any) =>
					Number(currencyData.totalAmount) *
					Number(currencyData.currencyId?.usdValue || 0)
			)
			.reduce((a, prev) => a + prev, 0);

		setState({
			...state,
			fixed: fixedSum,
			flexible: flexiSum,
			stake: stakeSum,
		});
	}, [today]);

	return (
		<LandingStyle>
			<Card>
				<h1>
					Welcome
					{retrieveToken()?.user?.fullName && (
						<span>, {retrieveToken()?.user?.fullName}</span>
					)}
				</h1>
				<UrgentNotifications />
			</Card>

			<br />

			<div className="two-status">
				<Card>
					<h2>Withdrawal Request Status</h2>
					<StatusCount />
					<br />
					<Link to="/transactions/withdraw-requests">Withdrawal Requests</Link>
				</Card>

				<Card>
					<h2>Investment Withdrawals Status</h2>
					<InvestmentWithdrawalStatusCount
						statusCount={investmentWithdrawalStatus}
					/>
					<br />
					<Link to="/core-modules/investment/withdrawals">
						Investment Withdrawal Requests
					</Link>
				</Card>
			</div>

			<br />

			<div className="two-status">
				<Card>
					<h2>Swap Request Status</h2>
					<SwapStatusCount />
					<br />
					<Link to="/core-modules/swap">Swap Requests</Link>
				</Card>
				<Card>
					<h2>Loan Request Status</h2>
					<LoanStatusCount />
					<br />
					<Link to="/core-modules/loan-requests">Loan Requests</Link>
				</Card>
			</div>

			<br />

			<div className="two-status">
				<Card>
					<div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '16px' }}>
						<span className="material-icons" style={{ fontSize: '32px', color: '#1890ff' }}>schedule</span>
						<div>
							<h2 style={{ margin: 0 }}>Solid Cron</h2>
							<p style={{ margin: 0, color: '#8c8c8c', fontSize: '12px' }}>Backend Management Console</p>
						</div>
					</div>
					<div style={{ background: '#f9f9f9', padding: '12px', borderRadius: '8px', marginBottom: '16px' }}>
						<small style={{ color: '#595959' }}>Manage background tasks and scheduled processes for Solid services.</small>
					</div>
					<a 
						href="https://solid-cron.xend.africa/" 
						target="_blank" 
						rel="noopener noreferrer"
						style={{ 
							display: 'inline-flex', 
							alignItems: 'center', 
							gap: '5px',
							color: '#1890ff',
							fontWeight: 500
						}}
					>
						Access Console <span className="material-icons" style={{ fontSize: '14px' }}>open_in_new</span>
					</a>
				</Card>
				<Card>
					<div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '16px' }}>
						<span className="material-icons" style={{ fontSize: '32px', color: '#52c41a' }}>verified_user</span>
						<div>
							<h2 style={{ margin: 0 }}>System Health</h2>
							<p style={{ margin: 0, color: '#8c8c8c', fontSize: '12px' }}>Operational Monitoring</p>
						</div>
					</div>
					<div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
						<div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
							<span style={{ color: '#595959' }}>WhatsApp Bot</span>
							<Tag color="green">Active</Tag>
						</div>
						<div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
							<span style={{ color: '#595959' }}>Payment Gateway</span>
							<Tag color="blue">Operational</Tag>
						</div>
						<div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
							<span style={{ color: '#595959' }}>Aegis Security</span>
							<Tag color="cyan">Monitoring</Tag>
						</div>
					</div>
				</Card> 
			</div>

			<br />

			<div className="two-status">
				<Card>
					<div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '16px' }}>
						<span className="material-icons" style={{ fontSize: '32px', color: '#722ed1' }}>visibility</span>
						<div>
							<h2 style={{ margin: 0 }}>MPC Watcher</h2>
							<p style={{ margin: 0, color: '#8c8c8c', fontSize: '12px' }}>SwitchWallet Monitoring Tool</p>
						</div>
					</div>
					<div style={{ background: '#f9f9f9', padding: '12px', borderRadius: '8px', marginBottom: '16px' }}>
						<small style={{ color: '#595959' }}>Real-time monitoring tool for SwitchWallet MPC infrastructure.</small>
					</div>
					<a 
						href="https://solidstagingwatcher.switchwallet.io" 
						target="_blank" 
						rel="noopener noreferrer"
						style={{ 
							display: 'inline-flex', 
							alignItems: 'center', 
							gap: '5px',
							color: '#722ed1',
							fontWeight: 500
						}}
					>
						Launch Watcher <span className="material-icons" style={{ fontSize: '14px' }}>open_in_new</span>
					</a>
				</Card>
				<Card>
					<div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '16px' }}>
						<span className="material-icons" style={{ fontSize: '32px', color: '#25D366' }}>chat</span>
						<div>
							<h2 style={{ margin: 0 }}>WhatsApp Bot</h2>
							<p style={{ margin: 0, color: '#8c8c8c', fontSize: '12px' }}>Solid Pay Services</p>
						</div>
					</div>
					<div style={{ background: '#f9f9f9', padding: '12px', borderRadius: '8px', marginBottom: '16px' }}>
						<small style={{ color: '#595959' }}>Official WhatsApp bot for automated customer services and notifications.</small>
					</div>
					<a 
						href="https://solid-whatsapp-bot.xend.africa/" 
						target="_blank" 
						rel="noopener noreferrer"
						style={{ 
							display: 'inline-flex', 
							alignItems: 'center', 
							gap: '5px',
							color: '#25D366',
							fontWeight: 500
						}}
					>
						Connect Bot <span className="material-icons" style={{ fontSize: '14px' }}>open_in_new</span>
					</a>
				</Card>
			</div>

			<br />

			<div className="two-status">
				<Card>
					<h2>BVN EaseId Credit</h2>
					<div>
						<div style={{ display: 'flex', gap: '8px', marginBottom: '8px' }}>
							<p>Balance Amount:</p>
							<strong
								style={
									bvnEaseId?.balanceAmount > 150
										? { color: 'black' }
										: { color: 'red' }
								}
							>
								<NativeNumber
									value={bvnEaseId?.balanceAmount}
									decimalPlaces={4}
								/>
							</strong>
						</div>
						<div style={{ display: 'flex', gap: '8px', marginBottom: '8px' }}>
							<p>Payee Account:</p>
							<strong>6683425613</strong>
						</div>
						<div style={{ display: 'flex', gap: '8px', marginBottom: '8px' }}>
							<p>Payee Account Name:</p>
							<strong>Xend Tech Limited(EaseID)</strong>
						</div>
						<div style={{ display: 'flex', gap: '8px', marginBottom: '8px' }}>
							<p>Payee Bank:</p>
							<strong>PalmPay</strong>
						</div>
					</div>
				</Card>
				<Card>
					<h2>Payment Gateways</h2>

					<div>
						{paymentGateways?.length > 0 ? (
							paymentGateways.map((data: any) => (
								<div
									style={{
										borderBottom: '1px solid #d7d7d4',
										padding: '10px 0',
									}}
								>
									<div>{data?.key}</div>

									{data?.BELLBANK_DATA && (
										<div style={{ marginTop: '8px' }}>
											<div
												style={{
													display: 'flex',
													gap: '8px',
													marginTop: '8px',
												}}
											>
												<p>Available Balance:</p>
												{data?.BELLBANK_DATA?.availableBalance !== null &&
												data?.BELLBANK_DATA?.availableBalance !== undefined ? (
													<strong>
														<NativeNumber
															value={data?.BELLBANK_DATA?.availableBalance}
															decimalPlaces={2}
														/>{' '}
														{data?.BELLBANK_DATA?.currency}
													</strong>
												) : (
													'Balance not available'
												)}
											</div>

											<div
												style={{
													display: 'flex',
													gap: '8px',
													marginTop: '4px',
												}}
											>
												<p>Ledger Balance:</p>
												{data?.BELLBANK_DATA?.ledgerBalance !== null &&
												data?.BELLBANK_DATA?.ledgerBalance !== undefined ? (
													<strong>
														<NativeNumber
															value={data?.BELLBANK_DATA?.ledgerBalance}
															decimalPlaces={2}
														/>{' '}
														{data?.BELLBANK_DATA?.currency}
													</strong>
												) : (
													'Balance not available'
												)}
											</div>
										</div>
									)}

									{data?.FIDELITY_DATA && (
										<div>
											<div
												style={{
													display: 'flex',
													gap: '8px',
													marginTop: '8px',
												}}
											>
												<p>Available Balance:</p>
												{data?.FIDELITY_DATA?.availableBalance !== null &&
												data?.FIDELITY_DATA?.availableBalance !== undefined ? (
													<strong>
														<NativeNumber
															value={data?.FIDELITY_DATA?.availableBalance}
															decimalPlaces={2}
														/>{' '}
														{data?.FIDELITY_DATA?.currency}
													</strong>
												) : (
													'Balance not available'
												)}
											</div>
											<div
												style={{
													display: 'flex',
													gap: '8px',
													marginTop: '4px',
												}}
											>
												<p>Ledger Balance:</p>
												{data?.FIDELITY_DATA?.ledgerBalance !== null &&
												data?.FIDELITY_DATA?.ledgerBalance !== undefined ? (
													<strong>
														<NativeNumber
															value={data?.FIDELITY_DATA?.ledgerBalance}
															decimalPlaces={2}
														/>{' '}
														{data?.FIDELITY_DATA?.currency}
													</strong>
												) : (
													'Balance not available'
												)}
											</div>
										</div>
									)}

									{data?.DATA && (
										<div>
											<div
												style={{
													display: 'flex',
													gap: '8px',
													marginTop: '8px',
												}}
											>
												<p>Available Balance:</p>
												{data?.DATA?.availableBalance !== null &&
												data?.DATA?.availableBalance !== undefined ? (
													<strong>
														<NativeNumber
															value={data?.DATA?.availableBalance}
															decimalPlaces={2}
														/>{' '}
														{data?.DATA?.currency}
													</strong>
												) : (
													'Balance not available'
												)}
											</div>
											<div
												style={{
													display: 'flex',
													gap: '8px',
													marginTop: '4px',
												}}
											>
												<p>Ledger Balance:</p>
												{data?.DATA?.ledgerBalance !== null &&
												data?.DATA?.ledgerBalance !== undefined ? (
													<strong>
														<NativeNumber
															value={data?.DATA?.ledgerBalance}
															decimalPlaces={2}
														/>{' '}
														{data?.DATA?.currency}
													</strong>
												) : (
													'Balance not available'
												)}
											</div>
											<div
												style={{
													display: 'flex',
													gap: '8px',
													marginTop: '4px',
												}}
											>
												<p>Frozen Balance:</p>
												{data?.DATA?.frozenBalance !== null &&
												data?.DATA?.frozenBalance !== undefined ? (
													<strong>
														<NativeNumber
															value={data?.DATA?.frozenBalance}
															decimalPlaces={2}
														/>{' '}
														{data?.DATA?.currency}
													</strong>
												) : (
													'Balance not available'
												)}
											</div>
											<div
												style={{
													display: 'flex',
													gap: '8px',
													marginTop: '4px',
												}}
											>
												<p>Guaranteed Balance:</p>
												{data?.DATA?.guaranteedBalance !== null &&
												data?.DATA?.guaranteedBalance !== undefined ? (
													<strong>
														<NativeNumber
															value={data?.DATA?.guaranteedBalance}
															decimalPlaces={2}
														/>{' '}
														{data?.DATA?.currency}
													</strong>
												) : (
													'Balance not available'
												)}
											</div>
											<div
												style={{
													display: 'flex',
													gap: '8px',
													marginTop: '4px',
												}}
											>
												<p>UnSettle Balance:</p>
												{data?.DATA?.unSettleBalance !== null &&
												data?.DATA?.unSettleBalance !== undefined ? (
													<strong>
														<NativeNumber
															value={data?.DATA?.unSettleBalance}
															decimalPlaces={2}
														/>{' '}
														{data?.DATA?.currency}
													</strong>
												) : (
													'Balance not available'
												)}
											</div>
											<div
												style={{
													display: 'flex',
													gap: '8px',
													marginTop: '4px',
												}}
											>
												<p>Can Top Up:</p>
												{data?.DATA?.canTopUp ? (
													<strong>True</strong>
												) : (
													<strong>False</strong>
												)}
											</div>
											<div
												style={{
													display: 'flex',
													gap: '8px',
													marginTop: '4px',
												}}
											>
												<p>Account Number:</p>
												{data?.DATA?.accountNumber ? (
													<strong>{data?.DATA?.accountNumber}</strong>
												) : (
													'Account Number not available'
												)}
											</div>
										</div>
									)}
								</div>
							))
						) : (
							<p>No payment gateway</p>
						)}
					</div>
				</Card>
			</div>

			<br />

			<Card>
				<h2>Today&apos;s Stats</h2>
				<div className="todays-stats">
					<div>
						<p className="stat-label">User Registration</p>
						<p className="stat-value" style={{ display: 'flex' }}>
							<span
								className="material-icons"
								style={{ fontSize: 16, marginRight: 5 }}
							>
								group_add
							</span>{' '}
							<NativeNumber value={today.users.today} decimalPlaces={4} />
						</p>
						<div style={{ width: '80%' }}>
							<div className="amount">
								<span
									className="material-icons"
									style={{ fontSize: 16, marginRight: 2 }}
								>
									public
								</span>
								<p>
									<NativeNumber value={today.users.total} decimalPlaces={4} />
									<br /> verified users
								</p>
							</div>
						</div>
					</div>
					<div>
						<p className="stat-label">Fixed Savings</p>
						<p className="stat-value">
							$<NativeNumber value={state.fixed} decimalPlaces={4} />
						</p>
						<div style={{ width: '90%' }}>
							{today.fixed.today.map((item: any) => (
								<div className="amount" key={item.currencyId?._id}>
									<img
										alt=""
										src={item.currencyId.iconUrl || defaultImage}
										width={20}
									/>
									<p>
										<NativeNumber value={item.totalAmount} decimalPlaces={4} />{' '}
										{item.currencyId.symbol}
									</p>
								</div>
							))}
						</div>
					</div>
					<div>
						<p className="stat-label">Flexible Savings</p>
						<p className="stat-value">
							$<NativeNumber value={state.flexible} decimalPlaces={4} />
						</p>
						<div style={{ width: '90%' }}>
							{today.flexible.today.map((item: any) => (
								<div className="amount" key={item.currencyId?._id}>
									<img
										alt=""
										src={item.currencyId.iconUrl || defaultImage}
										width={20}
									/>
									<p>
										<NativeNumber value={item.totalAmount} decimalPlaces={4} />{' '}
										{item.currencyId?.symbol}
									</p>
								</div>
							))}
						</div>
					</div>
					<div>
						<p className="stat-label">Staking</p>
						<p className="stat-value">
							$<NativeNumber value={state.stake} decimalPlaces={4} />
						</p>
						<div style={{ width: '90%' }}>
							{today.stakes.today.map((item: any) => (
								<div className="amount" key={item.currencyId?._id}>
									<img
										alt=""
										src={item.currencyId.iconUrl || defaultImage}
										width={20}
									/>
									<p>
										<NativeNumber value={item.totalAmount} decimalPlaces={4} />{' '}
										{item.currencyId?.symbol}
									</p>
								</div>
							))}
						</div>
					</div>
				</div>
			</Card>

			<br />

			<Card>
				<TodaysApy />
			</Card>

			<br />

			<Card>
				<ReportHighlights />
			</Card>

			<br />

			<Card>
				<h2>Custodial Wallets</h2>
				{custodialWallets.itemsList.map((wallet: any) => (
					<CustodialWallets key={wallet._id} wallet={wallet} />
				))}
				<br />
				<Link
					style={{ textTransform: 'uppercase' }}
					to="/settings/general#custodial-wallets"
				>
					Manage
				</Link>
			</Card>
			<br />
			<div className="two-status">
				<CustodialUsers />
				<OnlineAdmins />
			</div>
		</LandingStyle>
	);
};

export default LandingPage;
